import { EChartsOption } from 'echarts'
import ReactEcharts from 'echarts-for-react'
import { useTranslation } from 'react-i18next'

import { ScalrCarousel } from '@/components/customComponents/ScalrCarousel'
import { chartColors } from '@/constants/COLOR_PALETTE'
import { formatNumber } from '@/utils'

export interface KpiRealizationItem {
  title: string
  value: number
  maxValue: number
}

interface BonusPayoutChartProps {
  value: number
  min?: number
  max?: number
  unit?: string
  splitNumber?: number
  color?: string
  shadowColor?: string
  width?: number | string
  height?: number | string
  title?: string
  showMaxPayout?: boolean
  kpiRealizations?: KpiRealizationItem[]
}

const BonusPayoutChart: React.FC<BonusPayoutChartProps> = ({
  value,
  min = 0,
  max = 240,
  unit = '$',
  splitNumber = 12,
  width = '100%',
  height = '100%',
  showMaxPayout = true,
  kpiRealizations = [],
}) => {
  const { t } = useTranslation()
  const isGoalReached = value >= max
  const maxPayoutLabel =
    showMaxPayout &&
    `${t('dashboard.bonusPayoutMaxPayout')}: ${unit}${formatNumber(max, 'number')}`
  const motivationalText = isGoalReached
    ? t('dashboard.bonusPayoutSuccess')
    : t('dashboard.bonusPayoutAlmostThere')

  const option: EChartsOption = {
    graphic: [
      ...(maxPayoutLabel
        ? [
            {
              type: 'text' as const,
              right: '8%',
              bottom: '40%',
              style: {
                text: maxPayoutLabel,
                fontSize: 12,
                fill: '#6b7280',
              },
            },
          ]
        : []),
    ],
    series: [
      {
        type: 'gauge',
        z: 1,
        center: ['50%', '50%'],
        startAngle: 180,
        endAngle: 0,
        min: min,
        max: max,
        splitNumber: splitNumber,
        itemStyle: {
          color: chartColors[0],
        },
        progress: {
          show: true,
          width: 18,
          roundCap: true,
        },
        axisLine: {
          lineStyle: {
            width: 18,
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        anchor: {
          show: false,
          showAbove: true,
          size: 25,
          itemStyle: {
            borderWidth: 10,
          },
        },
        title: {
          show: false,
        },
        detail: {
          width: '60%',
          lineHeight: 40,
          height: 40,
          offsetCenter: [0, '-25%'],
          valueAnimation: true,
          formatter: function (val: number) {
            return '{text|' + formatNumber(val, 'money') + '}'
          },
          rich: {
            text: {
              fontSize: 28,
              color: '#000',
            },
          },
        },
        data: [
          {
            value: value,
          },
        ],
        splitLine: {
          distance: -18,
          show: true,
          length: 18,
          lineStyle: {
            width: 4,
            color: '#FFF',
          },
        } as any,
        pointer: {
          show: false,
        },
      },
      // Chart exists solely to show splitLine on top
      {
        type: 'gauge',
        z: 10,
        center: ['50%', '50%'],
        startAngle: 180,
        endAngle: 0,
        min: min,
        max: max,
        splitNumber: splitNumber,
        progress: {
          show: false,
        },
        axisLine: {
          show: false,
          lineStyle: {
            width: 18,
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
        anchor: {
          show: false,
        },
        title: {
          show: false,
        },
        detail: {
          show: false,
        },
        data: [
          {
            value: value,
          },
        ],
        splitLine: {
          distance: -18,
          show: true,
          length: 18,
          lineStyle: {
            width: 4,
            color: '#FFF',
          },
        },
        pointer: {
          show: false,
        },
      },
    ],
  }

  const renderKpiCard = (item: KpiRealizationItem, compact?: boolean) => {
    const percent =
      item.maxValue > 0
        ? Math.min(100, Math.round((item.value / item.maxValue) * 100))
        : 0
    const isFullyRealized = percent >= 100
    return (
      <div
        key={item.title}
        className={`flex flex-col items-center justify-center border-r border-gray-200 last:border-r-0 py-3 px-2 ${
          compact ? 'text-sm min-w-[100px]' : 'min-w-0'
        }`}
      >
        <span className="text-gray-500 font-medium">{item.title}</span>
        <span className="font-bold text-black mt-1">
          {unit}
          {formatNumber(item.value, 'number')}
        </span>
        <span
          className={`mt-1 font-medium ${
            isFullyRealized ? 'text-green-600' : 'text-red-600'
          }`}
        >
          {t('dashboard.bonusPayoutRealized', { percent })}
        </span>
      </div>
    )
  }

  const hasKpis = kpiRealizations.length > 0
  const useCarousel = kpiRealizations.length > 3

  return (
    <div className="flex flex-col">
      <div
        style={{
          width: width,
          height: height,
          minWidth: '400px',
          minHeight: '350px',
        }}
      >
        <ReactEcharts
          option={option}
          style={{ width: '100%', height: '100%' }}
        />
        <p className="mt-[-100px] text-center text-lg text-gray-600">
          {motivationalText}
        </p>
      </div>
      {hasKpis && (
        <div className="mt-4 border-t border-gray-200 pt-3">
          {useCarousel ? (
            <ScalrCarousel>
              {kpiRealizations.map((item) => renderKpiCard(item, true))}
            </ScalrCarousel>
          ) : (
            <div
              className="grid gap-0"
              style={{
                gridTemplateColumns: `repeat(${kpiRealizations.length}, minmax(0, 1fr))`,
              }}
            >
              {kpiRealizations.map((item) => renderKpiCard(item))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default BonusPayoutChart
